import { useCallback, useEffect, useState } from 'react';
import { IonButton, IonNote, IonProgressBar, IonSpinner } from '@ionic/react';
import { Card } from './Shell';
import { ApiError } from '../lib/api';
import { isServerConfigured } from '../lib/server';
import {
  canInstall,
  checkForUpdate,
  downloadAndInstall,
  dueForAutoCheck,
  formatSize,
  markAutoChecked,
  openInstallSettings,
  updatesSupported,
  type UpdateInfo,
} from '../lib/update';

/**
 * Die Update-Karte.
 *
 * Zwei Auftritte, dieselbe Mechanik:
 *
 * - `variant="banner"` auf der Startseite. Prueft still im Hintergrund
 *   (hoechstens alle sechs Stunden) und zeigt sich nur, wenn es wirklich etwas
 *   Neues gibt. Ein Fehler bleibt hier unsichtbar - wer die App oeffnet, will
 *   losfahren, nicht ueber GitHub lesen.
 * - `variant="settings"` in den Einstellungen. Zeigt immer die installierte
 *   Version, prueft beim Oeffnen und sagt auch, wenn etwas schiefgeht.
 */
export function AppUpdate({ variant }: { variant: 'banner' | 'settings' }) {
  const [info, setInfo] = useState<UpdateInfo | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [installing, setInstalling] = useState(false);
  const [needsPermission, setNeedsPermission] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(async (fresh: boolean) => {
    setChecking(true);
    setError(null);
    try {
      const result = await checkForUpdate({ fresh });
      setInfo(result);
      markAutoChecked();
    } catch (failure) {
      setInfo(null);
      setError(
        failure instanceof ApiError && failure.status === 503
          ? 'Dieser Server bietet keine Updates an (UPDATE_CHECK ist dort abgeschaltet).'
          : failure instanceof ApiError && failure.status === 404
            ? 'Der Server findet keinen Release - stimmt GITHUB_REPO, und ist ein GITHUB_TOKEN hinterlegt?'
            : failure instanceof Error
              ? failure.message
              : 'Die Suche nach Updates ist fehlgeschlagen.',
      );
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    // Beim allerersten Start ist noch keine Serveradresse eingetragen - dann
    // gibt es niemanden zu fragen, und eine Fehlermeldung waere nur im Weg.
    if (!updatesSupported() || !isServerConfigured()) return;
    if (variant === 'banner' && !dueForAutoCheck()) return;
    void check(false);
  }, [check, variant]);

  async function install(): Promise<void> {
    if (!info?.release.apk) return;

    // Ohne die Erlaubnis "Unbekannte Apps installieren" bricht der Dialog von
    // Android kommentarlos ab - lieber vorher fragen als hinterher raetseln.
    if (!(await canInstall())) {
      setNeedsPermission(true);
      return;
    }

    setInstalling(true);
    setNeedsPermission(false);
    setError(null);
    setProgress(0);
    try {
      await downloadAndInstall(info.release, setProgress);
      // Ab hier uebernimmt Androids Installationsdialog. Die App bleibt
      // stehen, wo sie ist - wird die Installation abgebrochen, ist nichts
      // passiert und der Knopf laesst sich erneut druecken.
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Das Update ist fehlgeschlagen.');
    } finally {
      setInstalling(false);
      setProgress(null);
    }
  }

  if (!updatesSupported() || !isServerConfigured()) return null;

  const available = info?.available === true;

  if (variant === 'banner' && (!available || dismissed)) return null;

  const body = (
    <>
      {variant === 'settings' && (
        <>
          <h2>App-Version</h2>
          <p>
            Installiert ist Version <strong>{info ? info.currentName : '…'}</strong>
            {info && info.current > 0 ? ` (Build ${info.current})` : ''}.
            {available
              ? ' Es gibt eine neuere.'
              : info && !checking && !error
                ? ' Das ist die neueste.'
                : ''}
          </p>
        </>
      )}

      {variant === 'banner' && <h2>Neue Version verfügbar</h2>}

      {available && info && (
        <>
          <p>
            <strong>{info.release.name || info.release.tag}</strong>
            {info.release.apk?.size ? ` · ${formatSize(info.release.apk.size)}` : ''}
          </p>
          {info.release.notes && <IonNote className="r2f-update__notes">{info.release.notes}</IonNote>}

          {progress !== null && <IonProgressBar value={progress} />}

          <IonButton expand="block" disabled={installing} onClick={() => void install()}>
            {installing
              ? progress !== null && progress > 0
                ? `Wird geladen… ${Math.round(progress * 100)} %`
                : 'Wird geladen…'
              : 'Herunterladen und installieren'}
          </IonButton>

          {variant === 'banner' && (
            <IonButton fill="clear" size="small" expand="block" onClick={() => setDismissed(true)}>
              Später
            </IonButton>
          )}

          {needsPermission && (
            <>
              <IonNote>
                Android muss dieser App zuerst erlauben, Apps zu installieren. Der Schalter
                heißt „Unbekannte Apps installieren“ und gilt nur für R2F.
              </IonNote>
              <IonButton fill="outline" expand="block" onClick={() => void openInstallSettings()}>
                Einstellung öffnen
              </IonButton>
            </>
          )}
        </>
      )}

      {variant === 'settings' && (
        <>
          {error && <p style={{ color: 'var(--ion-color-danger, #ff6b6b)' }}>{error}</p>}
          <IonButton fill="clear" size="small" disabled={checking} onClick={() => void check(true)}>
            {checking ? <IonSpinner name="dots" /> : 'Jetzt nach Updates suchen'}
          </IonButton>
          <IonNote>
            Die App holt sich die APK des neuesten Builds über deinen Server. Installiert wird
            erst, wenn du den Dialog von Android bestätigst.
          </IonNote>
        </>
      )}
    </>
  );

  return <Card>{body}</Card>;
}
